import { defineComponent as _defineComponent } from 'vue'
import { ref, reactive, nextTick, inject, onMounted } from 'vue'
import { useApolloClient, useMutation } from '@vue/apollo-composable'
import { useI18n } from "@/i18n" // in vue3: import { useI18n } from 'vue-i18n'
import * as cdn from "@coredination/components"
import GoogleLogin from "vue-google-login"
import AuthenticateWithPassword from "../graphql/auth/authenticateWithPassword.graphql"
import AuthenticateWithGoogle from "../graphql/auth/authenticateWithGoogle.graphql"
import AcceptEulaWithSmartCraft from "../graphql/session/acceptEulaWithSmartcraft.graphql"
import authenticateWithPasswordMutation from '@/graphql/auth/authenticateWithPassword.graphql'
import Eula from "@/views/Eula.vue"
import NewEmailConfirmation from "../graphql/user/newEmailConfirmation.graphql"
import EmailConfirmation from "@/views/EmailConfirmation.vue"
import eventbus from "@/eventbus"  // in vue3: const eventbus = inject('eventbus')

import * as cdnComposable from "@/vue-apollo-composable"


export default /*#__PURE__*/_defineComponent({
  __name: 'Login',
  emits: [
    'logged-in'
],
  setup(__props, { emit }) {

const { t } = useI18n()
const apolloClient = useApolloClient()

const email = ref(localStorage.getItem('email') || "")
const password = ref("")
const passwordVisible = ref(false)
const errorMessage = ref<string | null>(null)
const loading = ref(false)
const error = ref<string | null>(null)
const showEula = ref(sessionStorage.getItem('smartcraft_token') ? true : false)
const showEmailConfirmation = ref(false)
const sentEmailConfirmation = ref(false)
const rememberMe = ref(localStorage.getItem('email') ? true : false)
const googleLoginParams = reactive({
    client_id: "88486882721-cac1osvjj8p1r1igak3l8j6v43kol2tl.apps.googleusercontent.com",
})

const emailRef = ref<HTMLInputElement | null>(null)
const passwordRef = ref<HTMLInputElement | null>(null)
const loginRef = ref<HTMLButtonElement | null>(null)



onMounted(() => {
    if (emailRef.value) {
        emailRef.value.focus()
    }
})

const onEmailEnter = () => {
    if (passwordRef.value) {
        passwordRef.value.focus()
    }
}

const onPasswordEnter = () => {
    if (email.value && password.value) {
        login()
    } else if (!email.value && emailRef.value) {
        emailRef.value.focus()
    }
}
const { mutate: loginMutation } = useMutation(authenticateWithPasswordMutation)
async function login(acceptLicenseVersion?: any, acceptLicenseLocale?: any) {
    try {
        error.value = ''
        errorMessage.value = ''
        loading.value = true
        const response = await loginMutation({
            user: email.value,
            password: password.value,
            appId: process.env.VUE_APP_FRONTEND_URL, // vue3: appId: import.meta.env.VITE_APP_FRONTEND_URL,
            acceptLicenseVersion: acceptLicenseVersion,
            acceptLicenseLocale: acceptLicenseLocale
        },{
            fetchPolicy: 'no-cache'
        })
        console.log('response', response)
        if (response?.data?.authenticateWithPassword?.token) {
            //localStorage.setItem('apollo-token', response.data.authenticateWithPassword.token)
            if (rememberMe) {
                localStorage.setItem('email', email.value)
            } else {
                localStorage.removeItem('email')
            }
            await cdn.apollo.onLogin(
                apolloClient.client,
                response.data.authenticateWithPassword.token
            )
            // run login stuff for the new composable apollo client as well, necessary for websockets to work
            await cdnComposable.onLogin(response.data.authenticateWithPassword.token)
            emit('logged-in')
            showEula.value = false
            //router.push('/home')
        }
        loading.value = false
    } catch (err) {
        console.log('error', err)
        loading.value = false
        error.value = err
        if (err.message.indexOf("license agreement") >= 0) {
            showEula.value = true
        } else if (err.message.indexOf("Account unconfirmed") >= 0) {
            showEmailConfirmation.value = true
        } else {
            errorMessage.value = err.message
        }
    }
}

const hideEula = () => {
    showEula.value = false
    tryAgain()
}

const tryAgain = () => {
    errorMessage.value = null
    nextTick(() => {
        if (emailRef.value) {
            emailRef.value.focus()
        }
    })
}

const { mutate: googleLoginMutation } = useMutation(AuthenticateWithGoogle)
const onGoogleSuccess = async (googleUser: any) => {
    console.log(googleUser)
    console.log(googleUser.getBasicProfile().getEmail())
    loading.value = true
    try {
        const response = await googleLoginMutation({
            email: googleUser.getBasicProfile().getEmail(),
            token: googleUser.getAuthResponse().id_token
        })
        if (response?.data?.authenticateWithGoogle?.token) {
            await cdn.apollo.onLogin(
                apolloClient.client,
                response.data.authenticateWithGoogle.token
            )
            eventbus.emit('logged-in')
        }
    } catch (error) {
        loading.value = false
        errorMessage.value = error.message
    }
}

const onGoogleFailure = async (error: any) => {
    console.error("google fail", error)
}

const microsoftLogin = () => {
    window.location.href = "https://login.microsoftonline.com/common/oauth2/v2.0/authorize?client_id=f1cdb52a-3eeb-4913-b882-5cc8d9d36c15&redirect_uri="+(process?.env?.VUE_APP_BACKEND_URL || "http://localhost:8080")+"/core/session/smartcraft/microsoft/app&response_type=code&scope=openid";
}

const { mutate: smartcraftAcceptEulaMutation } = useMutation(AcceptEulaWithSmartCraft)
const smartcraftAcceptEula = async (smartCraftToken: string, acceptLicenseVersion?: number, acceptLicenseLocale?: string) => {
    loading.value = true
    try {
        await smartcraftAcceptEulaMutation({
            smartCraftToken: smartCraftToken,
            acceptLicenseVersion: acceptLicenseVersion,
            acceptLicenseLocale: acceptLicenseLocale,
        })
        loading.value = false
        sessionStorage.removeItem("smartcraft_token")
        microsoftLogin()
    } catch (error) {
        loading.value = false
        sessionStorage.removeItem("smartcraft_token")
        showEula.value = false
        const errorMessage: string = "" + t("license_failed_generic")
        const options: any = {
            multiLine: true,
            icon: "warning",
            x: "center",
            y: "top"
        }
        // this.$toast.error(errorMessage, options)
    }
}

const { mutate: NewEmailConfirmationMutation } = useMutation(NewEmailConfirmation)
const sendNewEmailConfirmationLink = async () => {
    loading.value = true
    try {
        await NewEmailConfirmationMutation({
            email: email.value,
            password: password.value
        })
        loading.value = false
        sentEmailConfirmation.value = true
    } catch (error) {
        loading.value = false
        showEmailConfirmation.value = false
        const errorMessage: string = "" + t("sendNewLink_failed_generic")
        const options: any = {
            multiLine: true,
            icon: "warning",
            x: "center",
            y: "top"
        }
        //this.$toast.error(errorMessage, options)
        console.log(errorMessage)
    }
}

return { __sfc: true,t, apolloClient, email, password, passwordVisible, errorMessage, loading, error, showEula, showEmailConfirmation, sentEmailConfirmation, rememberMe, googleLoginParams, emailRef, passwordRef, loginRef, emit, onEmailEnter, onPasswordEnter, loginMutation, login, hideEula, tryAgain, googleLoginMutation, onGoogleSuccess, onGoogleFailure, microsoftLogin, smartcraftAcceptEulaMutation, smartcraftAcceptEula, NewEmailConfirmationMutation, sendNewEmailConfirmationLink, Eula, EmailConfirmation }
}

})